import React, { useEffect, useState } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import { ArrowLeftFromLine } from "lucide-react"
import { appStore } from "lib/state"
import { CollectionLoader, Modal, Titlebar } from "lib/components"
import { useDynamicLayout } from "lib/hooks"
import { build_new_statement, cn, queryTools, randoms, runConditionalLogic } from "lib/utils"
import { useCompositeQuery } from "lib/hooks"
import { useDispute } from "lib/units/dispute/hooks"
import { PresetsChooser } from "lib/units/dispute/presets"
import { StatementPreview, useStatements } from "."
import { ConditionalWrapper } from "./conditionals"
import { ContentWrapper } from "./content"


export default function StatementsList({ elemtype }:{ elemtype:string }){
  const { dspid } = useParams()
  const [ searchParams, setSearchParams ] = useSearchParams() 

  const { DynamicWidthLayout, toggleExpanded, expandedState } = useDynamicLayout({ init: "expanded" })

  const { store, AppStore }:any = appStore()
  const { update } = AppStore 

  const [ selectedPresets, setSelectedPresets ] = useState<any>({})
  const [ conditionalReportData, setConditionalReportData ] = useState<any>(null)
  const [ filteredStatements, setFilteredStatements ] = useState([])
  const [ modalElement, setModalElement ] = useState<any>(null)
  const [ expandedView, setExpandedView ] = useState(false)

  const { data:_Dispute } = queryTools.resource.parseQueryForApi("dispute.findById", { id: dspid }, { enabled: !!dspid })
  const { data:_Statements } = queryTools.resource.parseQueryForApi("statement.findAll", {})
  const { data:_ApiData } = queryTools.resource.parseQueryForApi("apidata.findAll", {})

  const { data:_DisputeData } = useCompositeQuery({
    ...selectedPresets, 
    dispute: dspid,
    callback: (data:any) => {
      if( data.result !== "success" ) throw new Error(data.msg)
      const { conditionalReports }:any = runConditionalLogic(filteredStatements, data.payload)
      setConditionalReportData(conditionalReports)
    }
  })

  const saveFn = queryTools.resource.save(["dispute"])
  
  const { addStatementToDispute } = useStatements({ elemtype })
  const { replaceElementSettings, replaceElementConfig } = useDispute({ saveFn })

  // useEffect(() => {
  //   console.log("selectedPresets", selectedPresets)
  // },[selectedPresets])

  useEffect(() => {
    if( searchParams.get("modal") !== null && searchParams.get("st") !== null ){
      setModalElement(searchParams.get("modal"))
    }
  },[searchParams.get("modal")])

  useEffect(() => {
    const statements = _Dispute?.elements.find( (elem:any) => elem.refstr === elemtype)?.settings.config
    const filtered = statements.filter( (st:any) => 
      st.parent.includes(store.statementLoadParams?.parent || "[root]")
      && st.level === store.statementLoadParams?.level || 1
      && st.parentResult === store.statementLoadParams?.parentResult || "any"
    )
    console.log(filtered)
    setFilteredStatements(filtered)
  },[_Dispute, searchParams.get("elem")])


  if( !_Dispute ) return <></>
  return (
    <div className="w-full h-full flex *:text-neutral-200 gap-5">

      <PresetsChooser 
        mode="preview" 
        selectFn={(presets:any) => setSelectedPresets(presets)} 
        autoExpand={false} 
        className="mb-4" 
      />

      <Modal 
        status={modalElement !== null ? "open" : "closed"} 
        onClose={(open:boolean) => {
          setSearchParams((prev) => {
            prev.delete("st")
            prev.delete("ctype")
            return prev
          })
          setModalElement(null)}
        }
        title="Statement Loader"
        size="statement"
      >
        { modalElement === "conditionals" ? (
          <ConditionalWrapper usage="local" elemtype={elemtype} />
        ) : (
          <ContentWrapper usage="local" elemtype={elemtype} />
        )}
      </Modal>


      <DynamicWidthLayout 
        className={cn("w-full h-full items-start", expandedState ? "gap-7" : "gap-2")}
        expandedWidths={[ "w-3/12", "w-9/12" ]} 
        collapsedWidths={[ "w-[2rem]", "w-[calc(100%-2rem)]" ]}
      >
        <CollectionLoader 
            title="Statement Loader"
            className="border border-solid border-slate-700 min-h-40"
            collection={_Statements || []} 
            labelProp="title" 
            showDescriptions={true}
            expanded={expandedState}
            toggleExpanded={toggleExpanded}
            selectFn={(statement:any) => {
              console.log(statement)
              const updatedStatements = addStatementToDispute(_Dispute, {...statement, refstr: randoms.string(12,"a#")}) 
              replaceElementConfig(_Dispute, elemtype, updatedStatements)
            }} 
          />

        <div className="flex flex-col gap-4 mb-3">
          <Titlebar 
            title="Statements"
            titleClass="text-xs uppercase tracking-wide"
            className="bg-slate-800 px-3"
            buttons={[
              { 
                label: "add", 
                onClick:() => {
                  const newStatement = build_new_statement()
                  const updatedStatements = addStatementToDispute(_Dispute, newStatement) 
                  replaceElementConfig(_Dispute, elemtype, updatedStatements)
                } 
              },
              { label: "toggle view", onClick:() => setExpandedView(!expandedView) },
            ]}
          />
          { filteredStatements.map( (st:any) => (
            <StatementPreview 
              key={st.refstr} 
              elemtype={elemtype} 
              statement={st} 
              report={conditionalReportData?.find((report:any) => report.statement === st.refstr)?.report || null }
              expanded={expandedView} 
              clickFn={(st:any, type:string, ctype?:string) => {
                setSearchParams((prev) => {
                  prev.set("st", st.refstr);
                  if( ctype ){
                    prev.set("ctype", ctype);
                  }
                  return prev;
                });
                setModalElement(type)
              }}
            />
          ))}
        </div>
      </DynamicWidthLayout>
    </div>
  )
}